import { useFormik } from "formik";
import { NumericFormat } from "react-number-format";
import { ModalComponent } from "../..";
import {
  formatDate,
  getCurrencySymbol,
  parseStringToNumber,
} from "../../../../helperFunctions";
import { useUploadContributionMutation } from "../../../../redux/features/contributions/contributions.api";
import { UploadContributionPayload } from "../../../../redux/features/contributions/contributions.type";
import { SingleCooperativeDetails } from "../../../../redux/features/cooperative/cooperative.type";
import {
  ManualPaymentMethods,
  ManualPaymentMethodsMap,
} from "../../../../utils/constants";
import { ManualPaymentMethod } from "../../../../utils/types";
import { uploadContributionSchema } from "../../../../validationSchema";
import ComboBox from "../../../inputs/combobox";
import { DateInput } from "../../../inputs/dateInput";
import { FileUploadInput } from "../../../inputs/fileUploadInput";
import { TextInput } from "../../../inputs/textInput";
import { TimeInput } from "../../../inputs/timeInput";
import { SuccessModal } from "../successModal";

export const UploadContributionModal = ({
  modal,
  modalOpen,
  handleModalClose,
  handleModalOpen,
  cooperativeDetails,
  onSuccess,
}: {
  modalOpen: boolean;
  handleModalClose: () => void;
  modal: string;
  handleModalOpen: (name: string) => void;
  cooperativeDetails: SingleCooperativeDetails;
  onSuccess: () => void;
}) => {
  const [uploadContribution, { isLoading: uploadingContribution }] =
    useUploadContributionMutation();

  const reset = () => {
    resetForm();
    handleModalClose();
  };

  const {
    values,
    handleBlur,
    errors,
    touched,
    setFieldValue,
    handleChange,
    isValid,
    resetForm,
    handleSubmit,
  } = useFormik({
    initialValues: {
      date: undefined,
      time: undefined,
      amount: "",
      file: undefined,
      method: undefined,
    },
    validationSchema: uploadContributionSchema,
    onSubmit: async (vals) => {
      const payload: UploadContributionPayload = {
        coop: `${cooperativeDetails.id}`,
        date: formatDate(vals.date, "YYYY-MM-DD"),
        amount: `${parseStringToNumber(vals.amount)}`,
        time: formatDate(vals.time, "hh:mm A"),
        file: vals.file as unknown as File,
        mode: vals.method as unknown as ManualPaymentMethod,
      };

      const formData = new FormData();

      Object.keys(payload).forEach((key) => {
        const typedKey = key as keyof typeof payload;
        const value = payload[typedKey];

        if (value !== null && value !== undefined) {
          formData.append(typedKey, value);
        }
      });

      const resp = await uploadContribution(formData);

      if (resp.data?.success) {
        reset();
        handleModalOpen("uploadContributionSuccessModal");
        onSuccess();
      }

      return;
    },
  });

  return (
    <>
      <ModalComponent
        isModalOpen={modalOpen && modal === "uploadContributionModal"}
        onModalClose={() => {
          reset();
        }}
        title="Upload Contribution"
        hasButtons
        isLoading={uploadingContribution}
        isProceedDisabled={uploadingContribution || !isValid}
        proceedButtonText="Submit for approval"
        onProceedClick={() => {
          handleSubmit();
        }}
        hasCancelButton={false}
      >
        <div className="flex flex-col gap-3">
          <NumericFormat
            suffix={undefined}
            prefix={getCurrencySymbol("NGN")}
            decimalScale={2}
            thousandSeparator
            allowLeadingZeros={false}
            customInput={TextInput}
            type="text"
            name={"amount"}
            value={values?.amount ?? ""}
            placeholder="Enter amount"
            onBlur={handleBlur}
            onChange={handleChange}
            hasIcon={false}
            errors={errors?.amount && touched?.amount ? errors?.amount : ""}
            inputClass=""
            className=""
            customIconPosition="left"
            hasTitle={false}
          />

          <ComboBox
            menuItems={ManualPaymentMethods.map((method) => {
              return {
                name: ManualPaymentMethodsMap[method],
                value: method,
              };
            })}
            immediate
            onSelectOption={(val) => {
              setFieldValue("method", val);
            }}
            menuClassName="!z-[999]"
            type="text"
            name={"method"}
            value={
              ManualPaymentMethodsMap[
                values.method as unknown as ManualPaymentMethod
              ]
            }
            placeholder="Payment method"
            onBlur={handleBlur}
            onChange={(e) => {}}
            readOnly
            hasIcon={false}
            errors={errors?.method && touched?.method ? errors?.method : ""}
            isDropdown
            position="bottom start"
          />

          <div className="grid grid-cols-2 gap-3">
            <DateInput
              name={"date"}
              date={values?.date}
              placeholder="Payment date"
              setDate={(val) => {
                setFieldValue("date", val);
              }}
              onBlur={handleBlur}
              errors={touched?.date && errors?.date ? errors?.date : ""}
              disableFuture
              dateDisplayFormat="YYYY-MM-DD"
            />

            <TimeInput
              name={"time"}
              date={values?.time}
              placeholder="Payment time"
              setDate={(val) => {
                setFieldValue("time", val);
              }}
              onBlur={handleBlur}
              errors={touched?.time && errors?.time ? errors?.time : ""}
            />
          </div>

          <FileUploadInput
            title={"proof of payment"}
            maxNumberOfFiles={1}
            handleChange={(files) => {
              setFieldValue("file", files[0]);
            }}
            selectedFiles={values.file ? [values.file] : undefined}
            errors={errors?.file ? errors?.file : ""}
            accept="image/*,.pdf"
          />
        </div>
      </ModalComponent>

      <SuccessModal
        message={"Contribution added successfully"}
        subText={
          "Your uploaded contribution will reflect in your account once approved."
        }
        isModalOpen={modalOpen && modal === "uploadContributionSuccessModal"}
        onModalClose={() => {
          reset();
        }}
      />
    </>
  );
};
